import { StepStatus } from '@common/enums/serviceWorkflow/stepStatus';
import { TransitionType } from '@common/enums/serviceWorkflow/transitionType';
import type { Color, IGraphSizes } from './helpers';
import type { IProcessWorkflowStep, IServiceWorkflowStep, IServiceWorkflowTransition } from './WorkflowGraph';
import { CornerToCornerLine } from './shapes/CornerToCornerLine';
import { HorizontalLine } from './shapes/HorizontalLine';
import { Loop } from './shapes/Loop';
import { VerticalLine } from './shapes/VerticalLine';

interface ITransitionProps {
    fromProcessWorkflowSteps?: IProcessWorkflowStep[];
    fromStep: IServiceWorkflowStep;
    graphSizes: IGraphSizes;
    toProcessWorkflowSteps?: IProcessWorkflowStep[];
    toStep: IServiceWorkflowStep;
    transition: IServiceWorkflowTransition;
}

export interface ILineProps {
    color: Color;
    fromStep: IServiceWorkflowStep;
    graphSizes: IGraphSizes;
    label: string | null;
    strokeWidth: number;
    toStep: IServiceWorkflowStep;
}

export function Transition(props: ITransitionProps) {
    const { transition, fromStep, toStep, fromProcessWorkflowSteps, toProcessWorkflowSteps, graphSizes } = props;

    let color: Color = transition.synchronous ? 'purple' : 'orange';

    if (
        fromProcessWorkflowSteps?.some((step) => step.status === StepStatus.finished) &&
        toProcessWorkflowSteps &&
        toProcessWorkflowSteps.length > 0
    ) {
        color = 'green';
    }
    if (
        fromProcessWorkflowSteps?.some((step) => step.status === StepStatus.finished) &&
        toProcessWorkflowSteps?.length === 0 &&
        transition.onlyIfOutputEquals
    ) {
        color = 'grey';
    }
    if (transition.type === TransitionType.disable) {
        color = 'red';
    }

    const lineProps = {
        color,
        fromStep,
        graphSizes,
        label: transition.onlyIfOutputEquals,
        strokeWidth: 4,
        toStep,
    };

    if (fromStep.id === toStep.id) {
        return <Loop {...lineProps} />;
    }

    const xDiff = Math.abs(fromStep.displayOptions.x - toStep.displayOptions.x);
    const yDiff = Math.abs(fromStep.displayOptions.y - toStep.displayOptions.y);
    if (xDiff > yDiff) {
        return <HorizontalLine {...lineProps} />;
    }

    if (xDiff < yDiff) {
        return <VerticalLine {...lineProps} />;
    }

    return <CornerToCornerLine {...lineProps} />;
}
